import React from "react";
import SHAPBarChart from "../predictive/SHAPBarChart";
import { formatFeatureLabel } from "../../utils/formatFeatureLabel";

export interface RiskBreakdownDriver {
  feature?: string;
  label: string;
  impact?: number;
}

export interface RiskBreakdownFactor {
  feature?: string;
  label: string;
  impact?: number;
  percentage: number;
}

export interface RiskBreakdownResponse {
  shipment_id?: string | null;
  route_id?: string | null;
  delay_probability?: number | null;
  delay_probability_pct?: number | null;
  predicted_class?: number | null;
  risk_bucket?: string | null;
  top_features?: Array<{ feature?: string; label?: string; shap_value?: number; value?: string | null }>;
  key_drivers?: RiskBreakdownDriver[];
  factor_weights?: RiskBreakdownFactor[];
}

export interface RiskRecommendationsResponse {
  shipment_id?: string | null;
  route_id?: string | null;
  primary_driver?: string | null;
  recommendations?: Array<{
    title?: string;
    why?: string;
    actions?: string[];
  }> | string | null;
}

interface RouteRiskBreakdownProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  delayPct: number;
  delayColor: string;
  showRiskGraph: boolean;
  onToggleView: () => void;
  panelRiskBreakdownLoading: boolean;
  panelRecommendations: RiskRecommendationsResponse | null;
  panelRecommendationsLoading: boolean;
  topFeatures: Array<{ feature?: string; label?: string; shap_value?: number; value?: string | null }>;
  recommendations: Array<{ title?: string; why?: string; actions?: string[] }>;
}

const getRiskSectionCopy = ({
  showRiskGraph,
  hasTopFeatures,
  panelRiskBreakdownLoading,
  hasRecommendations,
  panelRecommendationsLoading,
  primaryDriver,
}: {
  showRiskGraph: boolean;
  hasTopFeatures: boolean;
  panelRiskBreakdownLoading: boolean;
  hasRecommendations: boolean;
  panelRecommendationsLoading: boolean;
  primaryDriver?: string | null;
}) => {
  if (showRiskGraph) {
    if (hasTopFeatures || panelRiskBreakdownLoading) {
      return {
        title: "Top Risk Drivers (SHAP)",
        subtitle: "Factors contributing to this shipment risk prediction",
      };
    }

    return {
      title: "Risk Insight Status",
      subtitle: "Detailed SHAP drivers are not available for this selected route.",
    };
  }

  if (hasRecommendations || panelRecommendationsLoading) {
    return {
      title: "AI Recommendations",
      subtitle: `Primary Driver: ${formatFeatureLabel(primaryDriver)}`,
    };
  }

  return {
    title: "Recommendation Status",
    subtitle: "AI recommendations are not available for this selected route.",
  };
};

const renderRiskGraphContent = ({
  panelRiskBreakdownLoading,
  topFeatures,
}: Pick<RouteRiskBreakdownProps, "panelRiskBreakdownLoading" | "topFeatures">) => {
  if (panelRiskBreakdownLoading) {
    return <div style={{ fontSize: 14, color: "#6B7280" }}>Loading SHAP factors...</div>;
  }

  if (topFeatures.length > 0) {
    return (
      <div style={{ minHeight: 220 }}>
        <SHAPBarChart
          topFeatures={topFeatures}
          height={Math.max(200, topFeatures.length * 35)}
          showLegend={true}
        />
      </div>
    );
  }

  return null;
};

const renderRecommendationsContent = ({
  panelRecommendationsLoading,
  recommendations,
}: Pick<RouteRiskBreakdownProps, "panelRecommendationsLoading" | "recommendations">) => {
  if (panelRecommendationsLoading) {
    return <div style={{ fontSize: 14, color: "#6B7280" }}>Loading recommendations...</div>;
  }

  // Return null when there are no items — the section-level subtitle from
  // getRiskSectionCopy already communicates unavailability, so we must not
  // duplicate a "not available" message inside the content area.
  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {recommendations.map((rec, idx) => (
        <div key={rec.title ?? `rec-${idx}`} style={{ padding: 16, backgroundColor: "#F9FAFB", borderRadius: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 8 }}>
            {rec.title ?? "Recommendation"}
          </div>
          {rec.why && (
            <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 10, lineHeight: 1.5 }}>
              {rec.why}
            </div>
          )}
          {Array.isArray(rec.actions) && rec.actions.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                Actions
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, color: "#111827" }}>
                {rec.actions.map((action, actionIdx) => (
                  <li key={`${action}-${actionIdx}`} style={{ fontSize: 13, marginBottom: 4 }}>
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RouteRiskBreakdown = ({
  expanded,
  onToggleExpanded,
  delayPct,
  delayColor,
  showRiskGraph,
  onToggleView,
  panelRiskBreakdownLoading,
  panelRecommendations,
  panelRecommendationsLoading,
  topFeatures,
  recommendations,
}: RouteRiskBreakdownProps) => {
  const hasTopFeatures = topFeatures.length > 0;
  const hasRecommendations = recommendations.length > 0;
  const canToggleViews =
    (panelRiskBreakdownLoading || hasTopFeatures) &&
    (panelRecommendationsLoading || hasRecommendations);
  const { title: sectionTitle, subtitle: sectionSubtitle } = getRiskSectionCopy({
    showRiskGraph,
    hasTopFeatures,
    panelRiskBreakdownLoading,
    hasRecommendations,
    panelRecommendationsLoading,
    primaryDriver: panelRecommendations?.primary_driver,
  });

  return (
  <div style={{ border: "1px solid #EAEAEA", borderRadius: 12, overflow: "hidden" }}>
    <button
      onClick={onToggleExpanded}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        background: expanded ? "#F9FAFB" : "#fff",
        border: "none",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>Risk Score Breakdown</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6B7280"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    {expanded && (
      <div style={{ padding: "20px", borderTop: "1px solid #EAEAEA", backgroundColor: "#fff", display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Delay Probability</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: delayColor }}>{delayPct}%</span>
          </div>
          <div style={{ width: "100%", height: 12, backgroundColor: "#E5E7EB", borderRadius: 9999 }}>
            <div style={{ height: "100%", width: `${delayPct}%`, backgroundColor: delayColor, borderRadius: 9999, transition: "width 0.4s ease" }} />
          </div>
        </div>

        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
                {sectionTitle}
              </div>
              <div style={{ fontSize: 13, color: "#6B7280" }}>
                {sectionSubtitle}
              </div>
            </div>
            {canToggleViews && (
              <button
                onClick={onToggleView}
                style={{
                  background: "none",
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                {showRiskGraph ? "Show AI Recommendations" : "Show Factors Graph"}
              </button>
            )}
          </div>

          {showRiskGraph
            ? renderRiskGraphContent({ panelRiskBreakdownLoading, topFeatures })
            : renderRecommendationsContent({ panelRecommendationsLoading, recommendations })}
        </div>
      </div>
    )}
  </div>
  );
};

export default RouteRiskBreakdown;
